import { useState } from "react";
import type { ApiStatus, FilmProject } from "../types";
import type { CloudProjectSummary } from "../api/client";
import { formatRuntime } from "../utils/storyboard";
import { Icon } from "./Icon";

interface ProjectLibraryProps {
  currentProject: FilmProject;
  projects: CloudProjectSummary[];
  apiStatus: ApiStatus;
  cloudStorageReady: boolean;
  isLoading: boolean;
  message: string;
  onRefresh: () => void;
  onOpenProject: (projectId: string) => void;
  onReviewPackage: (projectId: string) => void;
  onDuplicateProject: (projectId: string) => void;
  onDeleteProject: (projectId: string) => void;
  onNewProject: () => void;
  onBackToWorkspace: () => void;
}

function formatDate(value?: string): string {
  if (!value) return "Not synced yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not synced yet";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
}

function goalLabel(value?: string): string {
  if (!value) return "Draft";
  return value
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() + part.slice(1))
    .join(" ");
}

function currentProjectSummary(project: FilmProject): CloudProjectSummary {
  const uploadedAssets = Object.values(project.uploadedAssets || {}).flat();
  const uploadedAssetCount = uploadedAssets.length;
  const pendingUploadCount = uploadedAssets.filter((asset) => asset.cloudStatus === "pending").length;
  const failedUploadCount = uploadedAssets.filter((asset) => asset.cloudStatus === "failed").length;
  const localOnlyAssetCount = uploadedAssets.filter((asset) => asset.cloudStatus === "local_only" || asset.localOnly).length;
  return {
    id: project.id,
    title: project.title,
    goal: project.goal,
    runtimeSeconds: project.runtimeSeconds,
    aspectRatio: project.aspectRatio,
    resolution: project.resolution,
    sceneCount: project.scenes.length,
    frameCount: project.scenes.reduce((total, scene) => total + scene.frames.length, 0),
    uploadedAssetCount,
    pendingUploadCount,
    failedUploadCount,
    localOnlyAssetCount,
    generatedFrameCount: project.scenes.reduce((total, scene) => total + scene.frames.filter((frame) => frame.generationStatus === "generated").length, 0),
    scriptFileName: project.scriptUpload?.fileName,
    productionPackageCount: project.productionPackages?.length || (project.lastProductionPackage ? 1 : 0),
    providerReady: Boolean(project.lastProductionPackage || project.productionPackages?.length)
  };
}

export function ProjectLibrary({
  currentProject,
  projects,
  apiStatus,
  cloudStorageReady,
  isLoading,
  message,
  onRefresh,
  onOpenProject,
  onReviewPackage,
  onDuplicateProject,
  onDeleteProject,
  onNewProject,
  onBackToWorkspace
}: ProjectLibraryProps) {
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const currentSummary = currentProjectSummary(currentProject);
  const savedProjects = projects.filter((project) => project.id !== currentProject.id);
  const storageLabel = cloudStorageReady ? "Cloud drafts" : apiStatus === "checking" ? "Checking storage" : "Browser local";

  function renderProjectCard(project: CloudProjectSummary, isCurrent = false) {
    const deletePending = confirmDeleteId === project.id;
    const packageCount = project.productionPackageCount || (project.providerReady ? 1 : 0);
    const uploadedAssetCount = project.uploadedAssetCount || 0;
    const generatedFrameCount = project.generatedFrameCount || 0;
    const assetSyncLabel = project.failedUploadCount
      ? `${project.failedUploadCount} upload failed`
      : project.pendingUploadCount
        ? `${project.pendingUploadCount} upload pending`
        : project.localOnlyAssetCount
          ? `${project.localOnlyAssetCount} local only`
          : uploadedAssetCount
            ? `${uploadedAssetCount} uploads`
            : generatedFrameCount
              ? `${generatedFrameCount} generated`
              : "No uploads";
    return (
      <article className={`project-card ${isCurrent ? "current" : ""}`} key={project.id}>
        <div className="project-card-main">
          <div className="project-thumb" aria-hidden="true">
            <Icon name={project.providerReady ? "play" : "boards"} />
          </div>
          <div>
            <span>{isCurrent ? "Current draft" : formatDate(project.updatedAt)}</span>
            <h2>{project.title || "Untitled Film"}</h2>
            <p>
              {goalLabel(project.goal || project.useCase)}
              {project.runtimeSeconds ? ` · ${formatRuntime(project.runtimeSeconds)}` : ""}
              {project.aspectRatio ? ` · ${project.aspectRatio}` : ""}
              {project.resolution ? ` · ${project.resolution}` : ""}
            </p>
          </div>
        </div>

        <div className="project-card-meta">
          <span>{project.sceneCount || 0} scenes</span>
          <span>{project.frameCount || 0} boards</span>
          <span>{assetSyncLabel}</span>
          <span>{packageCount ? `${packageCount} package${packageCount === 1 ? "" : "s"}` : project.scriptFileName || "No package yet"}</span>
        </div>

        <div className="project-card-actions">
          {isCurrent ? (
            <button className="primary-action" onClick={onBackToWorkspace} type="button">
              <Icon name="boards" />
              Continue
            </button>
          ) : (
            <button className="primary-action" onClick={() => onOpenProject(project.id)} type="button">
              <Icon name="boards" />
              Open
            </button>
          )}
          {packageCount ? (
            <button className="secondary-action package-action" onClick={() => onReviewPackage(project.id)} type="button">
              <Icon name="play" />
              Review Package
            </button>
          ) : null}
          <button className="secondary-action" onClick={() => onDuplicateProject(project.id)} type="button">
            <Icon name="plus" />
            Duplicate
          </button>
          <button
            className={`secondary-action ${deletePending ? "danger" : ""}`}
            onClick={() => {
              if (deletePending) {
                onDeleteProject(project.id);
                setConfirmDeleteId(null);
              } else {
                setConfirmDeleteId(project.id);
              }
            }}
            type="button"
          >
            {deletePending ? "Confirm delete" : "Delete"}
          </button>
        </div>
      </article>
    );
  }

  return (
    <section className="project-library" aria-label="Projects">
      <header className="project-library-header">
        <div>
          <span>Projects</span>
          <h1>Saved drafts</h1>
          <p>Reopen storyboard work, duplicate a direction, or start a clean project.</p>
        </div>
        <div className="project-library-actions">
          <button className="secondary-action" disabled={isLoading} onClick={onRefresh} type="button">
            <Icon name="download" />
            {isLoading ? "Refreshing" : "Refresh"}
          </button>
          <button className="primary-action" onClick={onNewProject} type="button">
            <Icon name="plus" />
            New Project
          </button>
        </div>
      </header>

      <div className={`project-storage-note ${cloudStorageReady ? "cloud" : apiStatus === "checking" ? "checking" : "local"}`}>
        <span aria-hidden="true" />
        <p>
          <strong>{storageLabel}</strong>
          {" "}
          {message || (cloudStorageReady ? "Projects are saved through the configured project store." : "This draft is still protected by browser autosave.")}
        </p>
      </div>

      <div className="project-list">
        {renderProjectCard(currentSummary, true)}
        {savedProjects.map((project) => renderProjectCard(project))}
      </div>

      {!isLoading && cloudStorageReady && savedProjects.length === 0 ? (
        <div className="project-empty-state">
          <strong>No other saved drafts yet.</strong>
          <span>Your current project will appear here after cloud autosave finishes.</span>
        </div>
      ) : null}
    </section>
  );
}
